import { defineComponent as _defineComponent } from 'vue'
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from "vue";
  import {
    Armchair,
    Bath,
    BedDouble,
    Car,
    Check,
    ChevronDown,
    FileText,
    Image as ImageIcon,
    MoveHorizontal,
    Upload,
    X,
  } from "lucide-vue-next";
  import {
    LandSubdivisionPackage,
    LandSubdivisionPackagePayload,
  } from "@/types/LandSubdivisionPackage";

  interface StageOption {
    _id: string;
    title: string;
  }
  interface LotOption {
    _id: string;
    title: string;
    stageId: string | { _id: string };
  }
  type PackageAssetType = "logo" | "hero" | "brochure";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LandSubdivisionPackageModal',
  props: {
    isOpen: { type: Boolean },
    packageData: {},
    stages: {},
    lots: {},
    saving: { type: Boolean }
  },
  emits: ["close", "save"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;
  const emit = __emit;

  const blankForm = (): LandSubdivisionPackagePayload => ({
    builderName: "",
    builderLogoUrl: "",
    builderLogoFileName: "",
    designName: "",
    facadeName: "",
    packagePrice: 0,
    bedroom: 0,
    bathroom: 0,
    living: 0,
    cars: 0,
    size: 0,
    heroImageUrl: "",
    heroImageFileName: "",
    brochureUrl: "",
    brochureFileName: "",
    lotIds: [],
  });
  const form = reactive<LandSubdivisionPackagePayload>(blankForm());
  const selectedStageId = ref("");
  const stageSelectOpen = ref(false);
  const stageSelectTrigger = ref<HTMLElement | null>(null);
  const stageOptionsStyle = ref<Record<string, string>>({});
  const logoFile = ref<File | null>(null);
  const heroFile = ref<File | null>(null);
  const brochureFile = ref<File | null>(null);
  const logoInput = ref<HTMLInputElement | null>(null);
  const heroInput = ref<HTMLInputElement | null>(null);
  const brochureInput = ref<HTMLInputElement | null>(null);
  const validationMessage = ref("");

  const logoDisplayName = computed(
    () =>
      logoFile.value?.name ||
      form.builderLogoFileName ||
      getFileNameFromUrl(form.builderLogoUrl)
  );
  const heroDisplayName = computed(
    () =>
      heroFile.value?.name ||
      form.heroImageFileName ||
      getFileNameFromUrl(form.heroImageUrl)
  );
  const brochureDisplayName = computed(
    () =>
      brochureFile.value?.name ||
      form.brochureFileName ||
      getFileNameFromUrl(form.brochureUrl)
  );

  const configurationFields = [
    { key: "bedroom" as const, label: "Bed", icon: BedDouble },
    { key: "bathroom" as const, label: "Bath", icon: Bath },
    { key: "living" as const, label: "Living", icon: Armchair },
    { key: "cars" as const, label: "Cars", icon: Car },
    { key: "size" as const, label: "Size (m²)", icon: MoveHorizontal },
  ];
  const lotStageId = (lot: LotOption) =>
    typeof lot.stageId === "object" ? lot.stageId._id : lot.stageId;
  const currentStageLots = computed(() =>
    props.lots.filter(
      (lot) => String(lotStageId(lot)) === String(selectedStageId.value)
    )
  );
  const selectedStageLabel = computed(() => {
    const selectedStage = props.stages.find(
      (stage) => stage._id === selectedStageId.value
    );

    return selectedStage ? `Stage ${selectedStage.title}` : "Select a stage";
  });
  const selectedCurrentStageCount = computed(
    () =>
      currentStageLots.value.filter((lot) => form.lotIds.includes(lot._id))
        .length
  );
  const allCurrentStageSelected = computed(
    () =>
      currentStageLots.value.length > 0 &&
      selectedCurrentStageCount.value === currentStageLots.value.length
  );
  const assignedStageGroups = computed(() =>
    props.stages
      .map((stage) => ({
        stageId: stage._id,
        stageTitle: stage.title,
        lots: props.lots.filter(
          (lot) =>
            String(lotStageId(lot)) === String(stage._id) &&
            form.lotIds.includes(lot._id)
        ),
      }))
      .filter((group) => group.lots.length > 0)
  );

  watch(
    () => [props.isOpen, props.packageData, props.stages],
    () => {
      if (!props.isOpen) return;

      Object.assign(form, blankForm(), props.packageData ?? {});
      form.lotIds = (props.packageData?.lotIds ?? []).map((id: unknown) =>
        typeof id === "object" && id !== null && "_id" in id
          ? String((id as { _id: unknown })._id)
          : String(id)
      );
      selectedStageId.value = props.stages[0]?._id ?? "";
      stageSelectOpen.value = false;
      logoFile.value = null;
      heroFile.value = null;
      brochureFile.value = null;
      validationMessage.value = "";
    },
    { immediate: true }
  );

  watch(stageSelectOpen, async (isOpen) => {
    if (!isOpen) return;

    await nextTick();
    updateStageOptionsPosition();
  });

  onMounted(() => {
    document.addEventListener("click", closeStageSelect);
    document.addEventListener("scroll", updateStageOptionsPosition, true);
    window.addEventListener("resize", updateStageOptionsPosition);
  });
  onBeforeUnmount(() => {
    document.removeEventListener("click", closeStageSelect);
    document.removeEventListener("scroll", updateStageOptionsPosition, true);
    window.removeEventListener("resize", updateStageOptionsPosition);
  });

  function closeStageSelect() {
    stageSelectOpen.value = false;
  }

  function selectStage(stageId: string) {
    selectedStageId.value = stageId;
    stageSelectOpen.value = false;
  }

  function updateStageOptionsPosition() {
    if (!stageSelectOpen.value || !stageSelectTrigger.value) return;

    const triggerRect = stageSelectTrigger.value.getBoundingClientRect();
    const viewportPadding = 8;
    const menuGap = 5;
    const desiredHeight = Math.min(props.stages.length * 32 + 12, 260);
    const availableBelow =
      window.innerHeight - triggerRect.bottom - menuGap - viewportPadding;
    const availableAbove = triggerRect.top - menuGap - viewportPadding;
    const opensAbove =
      availableBelow < Math.min(desiredHeight, 160) &&
      availableAbove > availableBelow;
    const availableHeight = opensAbove ? availableAbove : availableBelow;
    const maxHeight = Math.max(48, Math.min(desiredHeight, availableHeight));

    stageOptionsStyle.value = {
      left: `${triggerRect.left}px`,
      width: `${triggerRect.width}px`,
      maxHeight: `${maxHeight}px`,
      transformOrigin: opensAbove ? "bottom" : "top",
      "--stage-options-offset": opensAbove ? "4px" : "-4px",
      ...(opensAbove
        ? {
            top: "auto",
            bottom: `${window.innerHeight - triggerRect.top + menuGap}px`,
          }
        : {
            top: `${triggerRect.bottom + menuGap}px`,
            bottom: "auto",
          }),
    };
  }

  function toggleSelectAll() {
    const currentIds = currentStageLots.value.map((lot) => lot._id);

    if (allCurrentStageSelected.value) {
      form.lotIds = form.lotIds.filter((id) => !currentIds.includes(id));
    } else {
      form.lotIds = Array.from(new Set([...form.lotIds, ...currentIds]));
    }
  }

  function getFileNameFromUrl(url?: string): string {
    if (!url) return "";

    const encodedFileName = url.split(/[?#]/)[0].split("/").pop() ?? "";

    try {
      return decodeURIComponent(encodedFileName);
    } catch {
      return encodedFileName;
    }
  }

  function selectFile(type: PackageAssetType, event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;

    if (type === "logo") logoFile.value = file;

    if (type === "hero") heroFile.value = file;

    if (type === "brochure") brochureFile.value = file;
  }

  function removeFile(type: PackageAssetType) {
    if (type === "logo") {
      logoFile.value = null;
      form.builderLogoUrl = "";
      form.builderLogoFileName = "";

      if (logoInput.value) logoInput.value.value = "";
    }

    if (type === "hero") {
      heroFile.value = null;
      form.heroImageUrl = "";
      form.heroImageFileName = "";

      if (heroInput.value) heroInput.value.value = "";
    }

    if (type === "brochure") {
      brochureFile.value = null;
      form.brochureUrl = "";
      form.brochureFileName = "";

      if (brochureInput.value) brochureInput.value.value = "";
    }
  }

  function submit() {
    validationMessage.value = "";

    if (!form.builderName || !form.designName || form.packagePrice < 0) {
      validationMessage.value =
        "Builder, design name and package price are required.";
      return;
    }

    emit("save", {
      payload: { ...form, lotIds: [...form.lotIds] },
      logoFile: logoFile.value,
      heroFile: heroFile.value,
      brochureFile: brochureFile.value,
    });
  }

const __returned__ = { props, emit, blankForm, form, selectedStageId, stageSelectOpen, stageSelectTrigger, stageOptionsStyle, logoFile, heroFile, brochureFile, logoInput, heroInput, brochureInput, validationMessage, logoDisplayName, heroDisplayName, brochureDisplayName, configurationFields, lotStageId, currentStageLots, selectedStageLabel, selectedCurrentStageCount, allCurrentStageSelected, assignedStageGroups, closeStageSelect, selectStage, updateStageOptionsPosition, toggleSelectAll, getFileNameFromUrl, selectFile, removeFile, submit, computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch, get Armchair() { return Armchair }, get Bath() { return Bath }, get BedDouble() { return BedDouble }, get Car() { return Car }, get Check() { return Check }, get ChevronDown() { return ChevronDown }, get FileText() { return FileText }, get ImageIcon() { return ImageIcon }, get MoveHorizontal() { return MoveHorizontal }, get Upload() { return Upload }, get X() { return X }, get LandSubdivisionPackage() { return LandSubdivisionPackage }, get LandSubdivisionPackagePayload() { return LandSubdivisionPackagePayload } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})